import { getCachedKindeRegion } from '@/hooks/query/useQueryKindeProperties';
import { resolveUpdateChannel, type UpdateChannel } from '@/tauri/updater/channel';
import { isTauri } from '@/tauri/utils';
import { getBuildIdTraits } from '@/utils/appVersion';

/**
 * The authenticated desktop auto-update channel (APP-6116) — `uk`/`eu`/`stable`,
 * or undefined on web / before the region resolves. Sourced from the
 * AUTHENTICATED `kindeProperties.region` (the regulatory key — never
 * `getRegion()`, C10) in the React Query cache, mirroring the Rust updater's
 * `resolve_channel`. Used by the per-event deployment-inventory dim below
 * (`getDeploymentInventoryProps`). The Snowplow / Datadog `client_context`
 * setters live in `OpenFeatureContextSync`, which resolves the same channel
 * from the same authenticated region — so every destination agrees.
 */
const getDesktopUpdateChannel = (): UpdateChannel | undefined => {
  if (!isTauri()) return undefined;
  return resolveUpdateChannel(getCachedKindeRegion());
};

/**
 * Desktop deployment-inventory dimensions (APP-6116 §5.11). Emitted as per-event
 * properties (via `extendedProps`) so they ride on every Amplitude / PostHog /
 * Datadog-RUM-action / Segment event WITHOUT an identify / setUser call — the
 * device-dimension routing discipline keeps these off person-properties so a
 * web↔desktop identity can't clobber another platform's value (last-write-wins).
 *
 * Desktop-only by design: the inventory answers "who runs which desktop build on
 * which channel". `build_id` is the baked CI short SHA (absent on web / local
 * builds). `update_channel` is runtime, derived from the AUTHENTICATED
 * `kindeProperties.region` (the regulatory key — never `getRegion()`, which fails
 * open / honours `?region=`, C10) read from the React Query cache; it mirrors the
 * Rust updater's `resolve_channel`. Both keys are never caller-supplied, so the
 * helper is spread AFTER the event props (system value wins, like installer_type).
 *
 * `region` is deliberately NOT a per-event key here: it is an overloaded event
 * prop (e.g. Evidence events pass `region` for the evidence-content region, not
 * the user's). The authenticated region already reaches analytics via the
 * `identify` person-trait + the Flagsmith `region` trait; `update_channel` is the
 * unambiguous per-event compliance partition (uk/eu/stable).
 */
// Exported for the analytics wiring (src/lib/analytics/wiring.ts), which
// injects these as context getters into the @heidi/runtime/analytics pipeline.
export const getDeploymentInventoryProps = (): {
  build_id?: string;
  update_channel?: UpdateChannel;
} => {
  if (!isTauri()) return {};
  const updateChannel = getDesktopUpdateChannel();
  return {
    ...getBuildIdTraits(),
    ...(updateChannel ? { update_channel: updateChannel } : {}),
  };
};
